// Care plan for agencies — which document in the child's Drive folder the
// contracting agency that staffs them may open from its staffing board.
//
// The owner chose the Nurse Assessment + Care Plan already kept in Drive
// (2026-10-27). Its name varies too much from child to child to pick
// automatically, so a coordinator chooses it here; likely matches are listed
// first. An agency sees it only for a child on its own board — assigned,
// offered to it, or its nurses on the child's calendar — and
// get-agency-care-plan checks that again every time the file is opened.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { supabase, addActivityLog } from '../../lib/supabase';
import { listClientFolder } from '../../lib/driveListing';
import { useAuth } from '../../hooks/useAuth';
import { agenciesSeeingClient, agencyMayUsePortal } from '../../lib/agencySlice';
import { offerSummary, offerStatusText, extendOfferUpdate } from '../../lib/agencyOffers';
import OfferAgencyModal from '../scheduling/OfferAgencyModal';

const card = { background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16 };
const PDF = 'application/pdf';
const GDOC = 'application/vnd.google-apps.document';
const servable = (f) => f.mimeType === PDF || f.mimeType === GDOC;
const LOOKS_LIKE_CARE_PLAN = /care\s*_?-?plan|careplan|nurse\s*_?-?ass|NA\s*(\+|plus|and)\s*care/i;

/** Every file in the folder tree, with the folder path it sits in. */
const allFiles = (tree) => {
  const out = (tree?.files || []).map((f) => ({ ...f, path: '' }));
  const walk = (folder, path) => {
    const here = path ? `${path} / ${folder.name}` : folder.name;
    (folder.files || []).forEach((f) => out.push({ ...f, path: here }));
    (folder.subfolders || []).forEach((sf) => walk(sf, here));
  };
  (tree?.folders || []).forEach((f) => walk(f, ''));
  return out;
};

/** Each agency that may use the portal, with whether it sees this child and why (lib/agencySlice.js). */
const loadVisibility = async (clientId) => {
  const { data, error } = await supabase.from('contracting_agencies').select('id, name, status').order('name');
  if (error) throw new Error(error.message);
  const seeing = await agenciesSeeingClient(supabase, clientId);
  return (data || []).filter(a => agencyMayUsePortal(a.status)).map(a => {
    const e = seeing.get(a.id) || { offerId: null, offer: null, why: [] };
    return { ...a, offer: e.offer || null, why: e.why };
  });
};

export default function AgencyCarePlanCard({ client, onOffersChanged }) {
  const { profile } = useAuth();
  const [plan, setPlan] = useState(undefined);       // the live row, null when none
  const [picking, setPicking] = useState(false);
  const [files, setFiles] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const canShare = ['admin', 'manager', 'coordinator'].includes(profile?.role);
  const [agencies, setAgencies] = useState([]);

  const load = useCallback(async () => {
    const { data, error } = await supabase.from('client_shared_care_plans')
      .select('id, drive_file_id, file_name, mime_type, shared_by_name, shared_at')
      .eq('client_id', client.id).is('withdrawn_at', null).maybeSingle();
    if (error) { setErr(error.message); setPlan(null); return; }
    setPlan(data || null);
    try { setAgencies(await loadVisibility(client.id)); } catch (e) { setErr(e.message); }
  }, [client.id]);

  // Offering goes through the one offer form (OfferAgencyModal): what is
  // needed, if anything, and how long they have to answer. Withdrawing ends
  // the agency's access to the child and the care plan at once.
  const [offering, setOffering] = useState(null);
  const offer = (agency) => setOffering({
    client: { id: client.id, name: `${client.first_name || ''} ${(client.last_name || '').charAt(0)}.`.trim() },
    agency: { id: agency.id, name: agency.name },
  });
  const extend = async (agency) => {
    setBusy(true); setErr('');
    const { error } = await supabase.from('client_agency_offers')
      .update(extendOfferUpdate(agency.offer)).eq('id', agency.offer.id).eq('status', 'open');
    if (error) setErr(error.message);
    await load(); onOffersChanged?.();
    setBusy(false);
  };
  const withdraw = async (agency) => {
    if (!window.confirm(`Withdraw the offer to ${agency.name}? Unless their nurses are scheduled with ${client.first_name}, they will no longer see ${client.first_name} or the care plan.`)) return;
    setBusy(true); setErr('');
    const { error } = await supabase.from('client_agency_offers')
      .update({ status: 'withdrawn', closed_at: new Date().toISOString(), closed_reason: `Withdrawn by ${profile?.full_name || 'the office'}`, updated_at: new Date().toISOString() })
      .eq('id', agency.offer.id);
    if (error) setErr(error.message);
    await load(); onOffersChanged?.();
    setBusy(false);
  };
  useEffect(() => { load(); }, [load]);

  const openPicker = async () => {
    setPicking(true); setErr('');
    if (files) return;
    try {
      const tree = await listClientFolder(client.client_id, client.drive_folder_id || null);
      setFiles(allFiles(tree));
    } catch (e) { setErr(e.message); setFiles([]); }
  };

  const ordered = useMemo(() => (files || []).slice().sort((a, b) => {
    const la = LOOKS_LIKE_CARE_PLAN.test(a.name) && servable(a) ? 0 : servable(a) ? 1 : 2;
    const lb = LOOKS_LIKE_CARE_PLAN.test(b.name) && servable(b) ? 0 : servable(b) ? 1 : 2;
    return la - lb || String(b.modifiedTime).localeCompare(String(a.modifiedTime));
  }), [files]);

  const share = async (f) => {
    setBusy(true); setErr('');
    try {
      const now = new Date().toISOString();
      if (plan) {
        const { error } = await supabase.from('client_shared_care_plans')
          .update({ withdrawn_at: now, withdrawn_by_name: profile?.full_name || null }).eq('id', plan.id);
        if (error) throw error;
      }
      const { error } = await supabase.from('client_shared_care_plans').insert([{
        client_id: client.id, drive_file_id: f.id, file_name: f.name, mime_type: f.mimeType,
        shared_by: profile?.id || null, shared_by_name: profile?.full_name || null,
      }]);
      if (error) throw error;
      await addActivityLog({
        client_id: client.id, activity_type: 'General Note',
        notes: `Care plan shared with contracting agencies: ${f.name}${plan ? ` (replacing ${plan.file_name})` : ''}.`,
        performed_by_id: profile?.id || null, performed_by_name: profile?.full_name || 'Unknown',
      });
      setPicking(false);
      await load();
    } catch (e) { setErr(e.message || 'Could not share the care plan.'); }
    setBusy(false);
  };

  const stop = async () => {
    if (!plan || !window.confirm(`Stop sharing ${plan.file_name}? Agencies will no longer be able to open it.`)) return;
    setBusy(true); setErr('');
    const { error } = await supabase.from('client_shared_care_plans')
      .update({ withdrawn_at: new Date().toISOString(), withdrawn_by_name: profile?.full_name || null }).eq('id', plan.id);
    if (error) setErr(error.message);
    else {
      await addActivityLog({
        client_id: client.id, activity_type: 'General Note',
        notes: `Care plan no longer shared with contracting agencies: ${plan.file_name}.`,
        performed_by_id: profile?.id || null, performed_by_name: profile?.full_name || 'Unknown',
      });
      await load();
    }
    setBusy(false);
  };

  if (plan === undefined) return null;

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 14 }}>Care plan for agencies</h3>
        {canShare && !picking && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-outline btn-sm" onClick={openPicker} disabled={busy}>{plan ? 'Change file' : 'Choose from Drive'}</button>
            {plan && <button className="btn btn-outline btn-sm" onClick={stop} disabled={busy}>Stop sharing</button>}
          </div>
        )}
      </div>

      {plan ? (
        <div style={{ fontSize: 12.5, color: 'var(--text2)', marginTop: 8, lineHeight: 1.5 }}>
          <b style={{ color: 'var(--text)' }}>{plan.file_name}</b>
          {' · '}shared {plan.shared_by_name ? `by ${plan.shared_by_name} ` : ''}on {format(new Date(plan.shared_at), 'MMM d, yyyy')}
          {' · '}<a href={`https://drive.google.com/file/d/${plan.drive_file_id}/view`} target="_blank" rel="noreferrer">Open in Drive</a>
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: 'var(--text3)', marginTop: 8 }}>Not shared. Agencies see no care plan for this child.</div>
      )}
      <div style={{ marginTop: 10, borderTop: '0.5px solid var(--border)' }}>
        {agencies.map(a => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '0.5px solid var(--border)', fontSize: 12.5, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 500 }}>{a.name}</span>
            {a.why.length
              ? <span style={{ color: 'var(--success)' }}>sees {client.first_name}{plan ? ' and the care plan' : ''} · {a.why.join(', ')}</span>
              : <span style={{ color: 'var(--text3)' }}>cannot see {client.first_name}</span>}
            {a.offer && <span style={{ width: '100%', fontSize: 11.5, color: 'var(--text3)' }}>Offer: {offerSummary(a.offer)} · {offerStatusText(a.offer)}</span>}
            {canShare && (
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                {a.offer?.status === 'open' && <button className="btn btn-outline btn-sm" onClick={() => extend(a)} disabled={busy}>Extend 7 days</button>}
                {a.offer
                  ? <button className="btn btn-outline btn-sm" onClick={() => withdraw(a)} disabled={busy}>Withdraw offer</button>
                  : !a.why.length && <button className="btn btn-primary btn-sm" onClick={() => offer(a)} disabled={busy}>Offer to {a.name.replace(/\s+(LLC|Inc\.?)$/i, '')}</button>}
              </span>
            )}
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 6, lineHeight: 1.5 }}>
        An agency sees a child, and the care plan, only while the child is theirs: offered to them, assigned, or their nurses on the calendar.
        Every page is stamped with the agency's name, the date, and the child's name and date of birth; each opening is logged.
      </div>

      {picking && (
        <div style={{ marginTop: 12, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: 'var(--bg2)', fontSize: 12 }}>
            <span>Choose the care plan from {client.first_name}'s Drive folder. PDFs and Google Docs can be shared.</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setPicking(false)}>Cancel</button>
          </div>
          {files === null ? <div style={{ padding: 12, fontSize: 12.5, color: 'var(--text3)' }}>Reading the Drive folder…</div>
            : ordered.length === 0 ? <div style={{ padding: 12, fontSize: 12.5, color: 'var(--text3)' }}>No files in the Drive folder.</div>
            : (
              <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                {ordered.map(f => (
                  <button key={f.id} type="button" disabled={!servable(f) || busy} onClick={() => share(f)}
                    title={servable(f) ? 'Share this file' : 'Only a PDF or a Google Doc can be shared. Save a Word file as PDF in Drive first.'}
                    style={{ display: 'flex', gap: 10, alignItems: 'baseline', width: '100%', textAlign: 'left', padding: '8px 10px', border: 'none', borderTop: '0.5px solid var(--border)',
                      background: 'none', cursor: servable(f) ? 'pointer' : 'not-allowed', opacity: servable(f) ? 1 : 0.5, font: 'inherit', color: 'var(--text)' }}>
                    <span style={{ fontSize: 12.5, fontWeight: LOOKS_LIKE_CARE_PLAN.test(f.name) ? 600 : 400, minWidth: 0, overflowWrap: 'anywhere' }}>{f.name}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                      {[f.path, f.modifiedTime ? format(new Date(f.modifiedTime), 'MMM d, yyyy') : null].filter(Boolean).join(' · ')}
                    </span>
                  </button>
                ))}
              </div>
            )}
        </div>
      )}
      {err && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 8 }}>{err}</div>}
      {offering && (
        <OfferAgencyModal {...offering} profile={profile}
          onClose={() => setOffering(null)}
          onDone={async () => { setOffering(null); await load(); onOffersChanged?.(); }} />
      )}
    </div>
  );
}
